/**
 * AdminInbox — single ops panel combining the three new pipelines:
 *  • Waitlist signups (with CSV export + broadcast launcher)
 *  • Contact messages (with CSV export + topic filter)
 *  • Pending lake photo submissions (approve/reject + apply-to-lake)
 *
 * Single API hit per tab on mount + on refresh. All actions are admin-only.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Bell,
  Camera,
  Download,
  Check,
  X,
  Send,
  Loader2,
  RefreshCw,
  Filter,
  ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'waitlist', label: 'Waitlist',  icon: Bell },
  { id: 'contact',  label: 'Contact',   icon: Mail },
  { id: 'photos',   label: 'Lake Photos', icon: Camera },
];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const AdminInbox = ({ token }) => {
  const [tab, setTab] = useState('waitlist');

  return (
    <section
      className="mt-12 rounded-2xl border border-white/10 bg-[#0b0d0b] p-6 md:p-8"
      data-testid="admin-inbox"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-extrabold uppercase tracking-wider text-white">
          Inbox &amp; Pipelines
        </h2>
        <div className="flex items-center gap-1 rounded border border-white/10 bg-[#141714] p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`inbox-tab-${t.id}`}
              className={`flex items-center gap-2 rounded px-3 py-1.5 text-xs uppercase tracking-widest font-bold transition-colors ${
                tab === t.id
                  ? 'bg-[#c8b273] text-black'
                  : 'text-[#a8b0a8] hover:text-white'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'waitlist' && <WaitlistPanel token={token} />}
      {tab === 'contact'  && <ContactPanel  token={token} />}
      {tab === 'photos'   && <PhotosPanel   token={token} />}
    </section>
  );
};


// ============================================================
// Waitlist
// ============================================================
const WaitlistPanel = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [interest, setInterest] = useState('all');
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/waitlist/summary`, { headers }),
        axios.get(`${API}/admin/waitlist?limit=500${interest === 'all' ? '' : `&interest=${interest}`}`, { headers }),
      ]);
      setSummary(s.data);
      setRows(r.data.rows || []);
    } catch {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [interest]);

  const exportCsv = () => {
    const q = interest === 'all' ? '' : `?interest=${interest}`;
    window.open(`${API}/admin/waitlist.csv${q}`, '_blank');
  };

  return (
    <div>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        <div className="rounded-lg border border-white/10 bg-[#141714] p-3 col-span-2 md:col-span-1">
          <p className="data-label text-[#c8b273] text-[10px] mb-1">Total</p>
          <p className="text-2xl font-bold text-white" data-testid="waitlist-total">
            {summary?.total ?? '—'}
          </p>
        </div>
        {(summary?.by_interest || []).slice(0, 5).map((b) => (
          <button
            key={b.interest}
            onClick={() => setInterest(b.interest)}
            className={`rounded-lg border p-3 text-left transition-colors ${
              interest === b.interest
                ? 'border-[#c8b273]/60 bg-[#c8b273]/5'
                : 'border-white/10 bg-[#141714] hover:border-[#c8b273]/30'
            }`}
          >
            <p className="data-label text-[#a8b0a8] text-[10px] mb-1 truncate">{b.interest.replace(/_/g, ' ')}</p>
            <p className="text-xl font-bold text-white">{b.count}</p>
            <p className="text-[10px] text-[#6b7d6b]">{b.unique_emails} unique</p>
          </button>
        ))}
      </div>

      {/* Filter + actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={interest} onValueChange={setInterest}>
          <SelectTrigger className="w-[200px] bg-[#141714] border-white/10 text-white" data-testid="waitlist-interest-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0b0d0b] border-white/10">
            <SelectItem value="all">All interests</SelectItem>
            <SelectItem value="marketplace">Marketplace</SelectItem>
            <SelectItem value="bait_bundles">Bait bundles</SelectItem>
            <SelectItem value="brand_partnerships">Brand partnerships</SelectItem>
            <SelectItem value="fishery_bookings">Fishery bookings</SelectItem>
            <SelectItem value="member_deals">Member deals</SelectItem>
            <SelectItem value="shop">Shop</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={load} className="text-[#a8b0a8] hover:text-white" data-testid="waitlist-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
        <Button variant="ghost" onClick={exportCsv} className="text-[#a8b0a8] hover:text-white" data-testid="waitlist-export-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
        <BroadcastDialog token={token} defaultInterest={interest === 'all' ? 'marketplace' : interest} onSent={load} />
      </div>

      <TableRows
        loading={loading}
        rows={rows}
        empty="No signups yet for this interest."
        columns={[
          { key: 'created_at', label: 'When', render: (r) => fmt(r.created_at) },
          { key: 'email',      label: 'Email' },
          { key: 'interest',   label: 'Interest', render: (r) => r.interest.replace(/_/g, ' ') },
          { key: 'name',       label: 'Name',     render: (r) => r.name || '—' },
        ]}
        testIdPrefix="waitlist-row"
      />
    </div>
  );
};

const BroadcastDialog = ({ token, defaultInterest, onSent }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    interest: defaultInterest,
    subject: '',
    body_html: '',
    dry_run: true,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm((s) => ({ ...s, interest: defaultInterest })); }, [defaultInterest]);

  const submit = async (dry) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/waitlist/broadcast`,
        { ...form, dry_run: dry },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data);
      if (!dry) toast.success(`Sent ${data.sent} emails (${data.failed || 0} failed)`);
      else toast.success(`Preview: ${data.recipients} recipients`);
      if (!dry && onSent) onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-gold" data-testid="waitlist-broadcast-open">
          <Send className="w-4 h-4 mr-2" />
          Broadcast launch
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
            Broadcast to waitlist
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <p className="data-label text-[#a8b0a8] mb-1.5">Interest</p>
            <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
              <SelectTrigger className="bg-[#141714] border-white/10 text-white" data-testid="broadcast-interest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0b0d0b] border-white/10">
                <SelectItem value="marketplace">Marketplace</SelectItem>
                <SelectItem value="bait_bundles">Bait bundles</SelectItem>
                <SelectItem value="brand_partnerships">Brand partnerships</SelectItem>
                <SelectItem value="fishery_bookings">Fishery bookings</SelectItem>
                <SelectItem value="member_deals">Member deals</SelectItem>
                <SelectItem value="shop">Shop</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="data-label text-[#a8b0a8] mb-1.5">Subject</p>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="The Carp Cast marketplace is now live"
              className="bg-[#141714] border-white/10 text-white"
              data-testid="broadcast-subject"
            />
          </div>
          <div>
            <p className="data-label text-[#a8b0a8] mb-1.5">Body (HTML allowed)</p>
            <Textarea
              rows={6}
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              placeholder="<p>The wait is over...</p>"
              className="bg-[#141714] border-white/10 text-white"
              data-testid="broadcast-body"
            />
          </div>
          {result && (
            <div className="rounded border border-[#c8b273]/30 bg-[#c8b273]/5 p-3 text-xs text-[#cfd4cf]" data-testid="broadcast-result">
              <p><b>{result.dry_run ? 'Preview' : 'Sent'}</b> · recipients: {result.recipients}{result.sent !== undefined ? ` · sent: ${result.sent}` : ''}{result.skipped_already_sent ? ` · skipped (already sent): ${result.skipped_already_sent}` : ''}</p>
              {result.sample_emails && <p className="mt-1 text-[#6b7d6b]">Sample: {result.sample_emails.join(', ')}{result.recipients > result.sample_emails.length ? '…' : ''}</p>}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="ghost" onClick={() => submit(true)} disabled={busy} className="text-[#a8b0a8] hover:text-white" data-testid="broadcast-preview">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Preview recipients
            </Button>
            <Button onClick={() => submit(false)} disabled={busy || !form.subject || !form.body_html} className="btn-gold ml-auto" data-testid="broadcast-send">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send now
            </Button>
          </div>
          <p className="text-[10px] text-[#6b7d6b]">Send-now is gated by Resend being configured server-side. Each (interest, subject) is sent once per recipient.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ============================================================
// Contact
// ============================================================
const ContactPanel = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [topic, setTopic] = useState('all');
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/contact-messages/summary`, { headers }),
        axios.get(`${API}/admin/contact-messages?limit=200${topic === 'all' ? '' : `&topic=${topic}`}`, { headers }),
      ]);
      setSummary(s.data); setRows(r.data.rows || []);
    } catch { toast.error('Failed to load contact messages'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [topic]);

  const exportCsv = () => window.open(`${API}/admin/contact-messages.csv${topic === 'all' ? '' : `?topic=${topic}`}`, '_blank');

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="rounded-lg border border-white/10 bg-[#141714] p-3 col-span-2 md:col-span-1">
          <p className="data-label text-[#c8b273] text-[10px] mb-1">Total</p>
          <p className="text-2xl font-bold text-white" data-testid="contact-total">{summary?.total ?? '—'}</p>
          <p className="text-[10px] text-[#c8b273]">{summary?.new ?? 0} new</p>
        </div>
        {(summary?.by_topic || []).slice(0, 4).map((b) => (
          <button
            key={b.topic}
            onClick={() => setTopic(b.topic)}
            className={`rounded-lg border p-3 text-left transition-colors ${
              topic === b.topic ? 'border-[#c8b273]/60 bg-[#c8b273]/5' : 'border-white/10 bg-[#141714] hover:border-[#c8b273]/30'
            }`}
          >
            <p className="data-label text-[#a8b0a8] text-[10px] mb-1 truncate">{b.topic.replace(/_/g, ' ')}</p>
            <p className="text-xl font-bold text-white">{b.count}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={topic} onValueChange={setTopic}>
          <SelectTrigger className="w-[200px] bg-[#141714] border-white/10 text-white" data-testid="contact-topic-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0b0d0b] border-white/10">
            <SelectItem value="all">All topics</SelectItem>
            <SelectItem value="general_support">General Support</SelectItem>
            <SelectItem value="fisheries_partnership">Fisheries & Partnerships</SelectItem>
            <SelectItem value="brand_supplier">Brand & Supplier</SelectItem>
            <SelectItem value="report_problem">Report a Problem</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={load} className="text-[#a8b0a8] hover:text-white"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        <Button variant="ghost" onClick={exportCsv} className="text-[#a8b0a8] hover:text-white" data-testid="contact-export-csv"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
      </div>

      <div className="space-y-2" data-testid="contact-rows">
        {loading ? (
          <p className="text-[#8a948a] text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-[#8a948a] text-sm">No messages.</p>
        ) : rows.map((m) => (
          <details key={m.id} className="rounded border border-white/10 bg-[#141714] open:border-[#c8b273]/40" data-testid={`contact-row-${m.id}`}>
            <summary className="cursor-pointer list-none px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs text-[#8a948a]">
                  <span className="px-1.5 py-0.5 rounded bg-[#0b0d0b] text-[#c8b273] uppercase tracking-widest text-[10px]">{m.topic.replace(/_/g, ' ')}</span>
                  <span>{fmt(m.created_at)}</span>
                </div>
                <p className="text-white text-sm mt-1 truncate"><b>{m.name}</b> · {m.email}</p>
                {m.subject && <p className="text-xs text-[#a8b0a8] mt-0.5 truncate">{m.subject}</p>}
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[#6b7d6b]">expand</span>
            </summary>
            <div className="px-4 pb-3 text-sm text-[#cfd4cf] whitespace-pre-wrap leading-relaxed border-t border-white/5 pt-3">
              {m.message}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};


// ============================================================
// Photo submissions
// ============================================================
const PhotosPanel = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/photo-submissions?status=${status}&limit=200`, { headers });
      setRows(data.rows || []);
    } catch { toast.error('Failed to load submissions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const moderate = async (id, action, applyToLake = false) => {
    try {
      await axios.post(
        `${API}/admin/photo-submissions/${id}/moderate`,
        { status: action, apply_to_lake: applyToLake },
        { headers },
      );
      toast.success(`Submission ${action}${applyToLake ? ' & applied to lake cover' : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Moderation failed');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[200px] bg-[#141714] border-white/10 text-white" data-testid="photos-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0b0d0b] border-white/10">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={load} className="text-[#a8b0a8] hover:text-white"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      {loading ? (
        <p className="text-[#8a948a] text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-[#8a948a] text-sm">No submissions in this state.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="photos-grid">
          {rows.map((s) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-white/10 bg-[#141714] overflow-hidden"
              data-testid={`photo-${s.id}`}
            >
              <div className="aspect-video bg-black">
                <img src={s.image_url} alt={`submitted photo of ${s.lake_name || 'lake'}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 text-xs text-[#8a948a]">
                  <span className="font-bold text-white truncate">{s.lake_name}</span>
                  <span>{fmt(s.created_at)}</span>
                </div>
                <p className="text-xs text-[#a8b0a8]">by <span className="text-[#c8b273]">{s.username || '—'}</span></p>
                {s.caption && <p className="text-xs text-[#cfd4cf] line-clamp-2">{s.caption}</p>}
                {s.status === 'pending' && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" className="btn-gold" onClick={() => moderate(s.id, 'approved', true)} data-testid={`photo-approve-apply-${s.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1.5" /> Approve &amp; set cover
                    </Button>
                    <Button size="sm" variant="ghost" className="text-[#a8b0a8]" onClick={() => moderate(s.id, 'approved', false)} data-testid={`photo-approve-${s.id}`}>
                      Approve
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => moderate(s.id, 'rejected')} data-testid={`photo-reject-${s.id}`}>
                      <X className="w-3.5 h-3.5 mr-1.5" /> Reject
                    </Button>
                  </div>
                )}
                {s.status !== 'pending' && (
                  <span className={`inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
                    s.status === 'approved' ? 'bg-[#22c55e]/15 text-[#22c55e]' : 'bg-red-500/15 text-red-400'
                  }`}>
                    {s.status}
                  </span>
                )}
                <a href={s.image_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#6b7d6b] hover:text-[#c8b273]">
                  <ExternalLink className="w-3 h-3" /> open original
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};


// ============================================================
// Shared table renderer
// ============================================================
const TableRows = ({ rows, columns, loading, empty, testIdPrefix }) => {
  if (loading) return <p className="text-[#8a948a] text-sm">Loading…</p>;
  if (!rows.length) return <p className="text-[#8a948a] text-sm">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-white/10" data-testid={`${testIdPrefix}s`}>
      <table className="w-full text-sm">
        <thead className="bg-[#141714] text-[10px] uppercase tracking-widest text-[#8a948a]">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left px-4 py-2.5 font-bold">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-white/5 hover:bg-white/5" data-testid={`${testIdPrefix}-${r.id || i}`}>
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-2.5 text-[#cfd4cf]">
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminInbox;
